/**
 * 视频舱 hub 三段式重排(2026-09-12 老板拍板,形制对齐视觉舱 hub)。
 *
 * ═══ 页面从上到下只有三段,回答三个递进的问题 ═══════════════════════════════════
 *   ① **来源扁条 ×3**「我手上已经有料了」:产品库 / 爆款 / 模板。
 *      形制与 `PosterSourceEntries` 逐件相同(icon + 标题 + 一句话,无图)。
 *   ② **「先来一条」**「我没料,直接开拍」:自由创作 / 真人出镜两张大卡
 *      (= 视觉舱「先来一张」那两张的视频版,见 `VideoFreeCard`)。
 *   ③ **目的墙 8 卡分两组**「我知道要拍什么」:带货与获客 4 + 品牌与组织 4。
 *
 * ═══ 这一版删掉了什么,为什么 ═══════════════════════════════════════════════════
 *   · **hero 大卡**(「这里能出 · 1 条视频 · 4 个场景」):形制已由卡面角标逐张说了一遍,
 *     场景数对用户不是个决策信息 —— 与视觉舱 R2① 删 hero 同一个判断,首屏让给入口。
 *   · **「一个导演台,两种深度」说明长条**:它说的是实现(快出 / 精修),而这一页只回答
 *     「我要拍什么」;那颗「进导演台」还与下面每张卡的「立即创作」抢同一个动作。
 *   · **旧「或者,换个来源开始」四瓦条**:被 ① + ② 取代 —— 原来「自由创作」和
 *     「一键追爆」并排摆在同一行,一个是兜底、一个是来源,不是一个维度。
 *   · **追爆 / 仿拍两条并排**:两条落的是同一个页面、同一件事(贴链接拆结构),
 *     合成一条「从爆款开始」。catalog 里两张卡都留着(打标口径,见 catalog.ts 那段注释)。
 *
 * ★目的卡回到 8 张一档一卡(2026-08-10 的 4 组卡拆回):组名(「品牌实力」「活动招聘」)
 *   在引擎里不存在,点它落的是组默认档,想拍招聘还得进页再换一次。现在点哪张预置哪档。
 * ★卡墙副标仍**不写**「按你的行业排序」:没有行业输入,UI 不许承诺没做的事。
 */
import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Boxes, Flame, LayoutTemplate } from "lucide-react";

import { VIDEO_CARDS } from "@/entities/scene-card/catalog";
import {
  VIDEO_WALL_GROUPS,
  briefPurposeOf,
  videoSceneKeyOf,
  type BriefPurpose,
} from "@/entities/scene-card/model/briefPurpose";
import type { SceneCard } from "@/entities/scene-card/model/types";
import { SceneCapCard } from "@/entities/scene-card/ui/SceneCapCard";
import { useCompliance, useModuleVisible } from "@/contexts/AuthContext";
import { useStaggerReveal } from "@/shared/motion/useStaggerReveal";
import { cn } from "@/shared/lib/utils";
import { CARD_WALL_RADIUS, CARD_WALL_TOUCH } from "@/shared/ui/cardWall";
import { VideoFreeCard } from "./VideoFreeCard";

/** 「从产品库开始」落地的目的(1014 V6):带货片 = 引擎兜底档「产品带货」。 */
const PRODUCT_ENTRY_PURPOSE: BriefPurpose = "产品带货";

/** 区头:竖色条 + 标题 + 灰字副题 +(可选)右侧张数。与视觉舱 `PosterHubSections` 同形。 */
function SectionHead({ title, sub, count }: { title: string; sub: string; count?: number }) {
  return (
    <div className="mb-3 flex flex-wrap items-baseline gap-2">
      <span className="h-3.5 w-[3px] self-center rounded-sm bg-primary" />
      <h2 className="text-sm font-semibold">{title}</h2>
      <span className="text-xs text-muted-foreground">{sub}</span>
      {count !== undefined && (
        <span className="ml-auto text-2xs text-muted-foreground">{count} 种</span>
      )}
    </div>
  );
}

/**
 * 来源扁条一条(形制逐件对齐 `PosterSourceEntries`:9×9 图标底 + 标题 + 一句话)。
 * `data-video-source-tile` 是既有的定位钩子(单测 / e2e 都按它找),名字不动。
 */
function SourceTile({
  title,
  sub,
  icon,
  badge,
  onOpen,
}: {
  title: string;
  sub: string;
  icon: React.ReactNode;
  /** 右上角小灰徽标(如模板的「陆续上新」)。 */
  badge?: string;
  onOpen: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onOpen}
      data-video-source-tile={title}
      className={cn(
        // 排布/尺寸/圆角照视觉舱来源条;反馈动效仍用本舱既有的 `.scene-src-tile`
        // (三皮肤都调过、带 focus-visible 外框、reduce-motion 下只去运动不去功能)——
        // 换成视觉舱那版会把键盘焦点框一起丢掉。
        "relative flex items-center gap-3 border border-border/70 bg-card px-3.5 py-3 text-left",
        CARD_WALL_RADIUS,
        CARD_WALL_TOUCH,
        "scene-src-tile"
      )}
    >
      <span
        className="grid h-9 w-9 shrink-0 place-items-center rounded-[calc(var(--radius)*0.8)] bg-primary/10 text-primary"
        aria-hidden
      >
        {icon}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[13.5px] font-semibold">{title}</span>
        <span className="block text-2xs leading-snug text-muted-foreground">{sub}</span>
      </span>
      {badge && (
        <span className="shrink-0 self-start rounded-md bg-muted px-1.5 py-0.5 text-2xs font-medium text-muted-foreground">
          {badge}
        </span>
      )}
    </button>
  );
}

export function VideoHubSections() {
  const navigate = useNavigate();
  const explosiveVisible = useModuleVisible("ai_explosive_parse");
  // 1011 算法备案:真人出镜无国内实现,domesticOnly 租户隐藏入口(api 侧同时拒绝)
  const { domesticOnly } = useCompliance();

  const open = (card: SceneCard) => {
    if (!card.route) return;
    // A1(目的锁定):目的卡把选定目的写进 URL `?usage=<key>` —— 刷新 / 直链 / 分享都保住目的
    //   (创建页优先从 ?usage 还原)。scenePreset 仍随行做首跳加速(state 一到就先种,不等 URL 解析)。
    const preset = card.preset;
    const to =
      preset?.kind === "video"
        ? `${card.route}?usage=${briefPurposeOf(preset.purpose)!.key}`
        : card.route;
    navigate(to, preset ? { state: { scenePreset: preset } } : undefined);
  };

  // 目的卡 = 本舱产物(8 张);来源卡(追爆/仿拍)带 funnel 标,由上面的来源扁条代表。
  // ★判据用 `funnel` 而不是 `!preset`:分身舱的「声纹克隆」也没 preset 但确实是那个舱的产物,
  // 用 `!preset` 当判据会把这类卡一起误排(场景计数少报)—— 见 types.ts funnel。
  const purposeCards = VIDEO_CARDS.filter((c) => !c.funnel);
  // 组成员(引擎 label)→ 卡:两侧都过 `videoSceneKeyOf`,组表与卡表漂不开
  const cardOf = (purpose: BriefPurpose) =>
    purposeCards.find((c) => c.key === videoSceneKeyOf(purpose));

  /**
   * 「真人出镜」入口接线(2026-09-14 车 D 落 cast=real → 2026-09-15 P0.5 车 F 改指专属页)。
   *
   * ★0915 老板拍板追加真人出镜专属页(`/ai-video/real`,两步式:选出镜人→选玩法,
   *   无模型选择、引导简单直白)——这颗卡的落点从 `/ai-video/create?cast=real` 改到这里。
   *   cast=real 那条通用线**仍在**(`VideoCreatePage` D2~D4 的两道闸/三态可视化/
   *   verification 状态机都长在那边),保留作编排底座(幕二分镜等复杂功能靠它),不撤。
   */
  const openLiveAction = () => {
    navigate("/ai-video/real");
  };

  return (
    <div className="space-y-6">
      {/* ① 三条「我已经有料了」的路 —— 取代原来的页头按钮 + 四瓦条 */}
      <div className="grid gap-3 sm:grid-cols-3">
        <SourceTile
          title="从产品库开始"
          sub="选一个商品,直接拍它的带货片"
          icon={<Boxes className="h-[19px] w-[19px]" strokeWidth={1.8} />}
          /**
           * 落创建页而不是 `/products`:产品库页(产品树)对 TENANT_STAFF **不在路由白名单**
           * (`ProtectedRoute.roleRouteAccess`),员工点进去直接 403;而且那一页压根没有
           * 「去做视频」这个动作,选完品也没有出口。创建页的素材区里就挂着**同一个**
           * 产品库抽屉(`ProductImagePanel` → `ProductPickSheet`),选品即带卖点与产品图,
           * 是现成最短的一条路,不新造流程。
           * `videoEntry` 标记随手带上(与视觉舱 `posterEntry:"product"` 同范式),
           * 创建页据此进页即弹出产品库抽屉(1013 V6 接线,VideoCreatePage.productEntry)。
           * ★1014 V6 复修:同时带 `?usage=<产品带货>` —— 不带目的时创建页走「自由创作」
           *   精简页,素材行是 FreeMaterialRow(首帧/参考图),**根本没有产品库抽屉**,
           *   videoEntry 标记无处可接。卡面写的就是「直接拍它的带货片」,落「产品带货」目的流
           *   = 与目的墙点「产品带货」卡同一条现成路(目的可在页内改),素材行带产品库抽屉。
           */
          onOpen={() =>
            navigate(`/ai-video/create?usage=${briefPurposeOf(PRODUCT_ENTRY_PURPOSE)!.key}`, {
              state: { videoEntry: "product" },
            })
          }
        />
        {/* 爆款那条挂 module 门:没有 `ai_explosive_parse` 的租户点进去是空舱
            (与 catalog 里那两张导流卡同一个判据) */}
        {explosiveVisible && (
          <SourceTile
            title="从爆款开始"
            sub="一键追爆 —— 贴一条爆款链接,拆蓝图照它的结构拍"
            icon={<Flame className="h-[19px] w-[19px]" strokeWidth={1.8} />}
            onOpen={() => navigate("/ai-explosive")}
          />
        )}
        <SourceTile
          title="从模板开始"
          sub="挑一套现成模板,换成你的产品"
          icon={<LayoutTemplate className="h-[19px] w-[19px]" strokeWidth={1.8} />}
          badge="陆续上新"
          onOpen={() => navigate("/ai-video/templates")}
        />
      </div>

      {/* ② 先来一条:没目的也能开拍(= 视觉舱「先来一张」的视频版) */}
      <FirstClipSection
        onFree={() => navigate("/ai-video/create")}
        onLive={domesticOnly ? null : openLiveAction}
      />

      {/* ③ 目的墙:两组各 4 张,点卡带 scenePreset 进创建页 */}
      {VIDEO_WALL_GROUPS.map((g) => (
        <PurposeGroupSection
          key={g.key}
          groupKey={g.key}
          title={g.title}
          sub={g.sub}
          cards={g.members.map((m) => cardOf(m)).filter((c): c is SceneCard => !!c)}
          onOpen={open}
        />
      ))}
    </div>
  );
}

/** 「先来一条」:自己描述 / 带上真人出演。两张卡摆在与目的墙同一套栅格里。 */
/** onLive=null → 不出「真人出镜」卡(1011 domesticOnly)。 */
function FirstClipSection({ onFree, onLive }: { onFree: () => void; onLive: (() => void) | null }) {
  const ref = useRef<HTMLDivElement>(null);
  useStaggerReveal(ref);
  return (
    <section className="scene-wall" data-video-wall="first-clip">
      <SectionHead
        title="先来一条"
        sub={onLive ? "自己描述,或带上真人出演" : "自己描述,直接开拍"}
      />
      <div ref={ref} className="scene-grid">
        <VideoFreeCard variant="free" onOpen={onFree} />
        {onLive && <VideoFreeCard variant="live" onOpen={onLive} />}
      </div>
    </section>
  );
}

function PurposeGroupSection({
  groupKey,
  title,
  sub,
  cards,
  onOpen,
}: {
  groupKey: string;
  title: string;
  sub: string;
  cards: SceneCard[];
  onOpen: (card: SceneCard) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useStaggerReveal(ref);
  return (
    <section className="scene-wall" data-purpose-group={groupKey}>
      <SectionHead title={title} sub={sub} count={cards.length} />
      <div ref={ref} className="scene-grid">
        {cards.map((c, i) => (
          <SceneCapCard
            key={c.key}
            card={c}
            onOpen={() => onOpen(c)}
            index={i}
            recommended={c.key === "video_goods"}
          />
        ))}
      </div>
    </section>
  );
}
